import React, { useContext, useEffect, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import axios from 'axios';
import baseUrl from '../../../../BaseURL';
import { ContextData } from '../../../../context/Context';

export default function WalletPaymentStatus() {
  const { Person, accesstoken, setRefresh, setRefreshAmount } = useContext(ContextData);
  const [validation, setValidation] = useState(null);
  const [credited, setCredited] = useState(null);
  const token = Person?.token;
  const role = Person?.acc_level;

  useEffect(() => {
    if (!token || role !== '0' || !accesstoken) return;
    let cancelled = false;
    let timer;
    let checking = false;
    const pendingReferences = new Set();
    const seen = new Set();

    async function check() {
      if (cancelled || checking) return;
      clearTimeout(timer);
      checking = true;
      let delay = 30000;
      try {
        const { data } = await axios.get(`${baseUrl}/auth/wallet/recharges/status`, { withCredentials: true, headers: { authorization: `Bearer ${accesstoken}` }, timeout: 12000 });
        if (cancelled || data?.status !== true || !Array.isArray(data.pending)) return;
        const credits = Array.isArray(data.credits) ? data.credits : data.latestCredit ? [data.latestCredit] : [];
        for (const credit of [...credits].reverse()) {
          const recentlyPaid = Date.now() - new Date(credit.createdAt).getTime() < 86400000;
          if (!seen.has(credit.reference) && (pendingReferences.has(credit.reference) || recentlyPaid)) {
            seen.add(credit.reference);
            setCredited(credit);
            setRefresh(previous => !previous);
            setRefreshAmount(previous => !previous);
            window.dispatchEvent(new CustomEvent('fin-wallet-credited', { detail: { ...credit, balance: data.balance } }));
          }
        }
        pendingReferences.clear();
        data.pending.forEach(payment => pendingReferences.add(payment.reference));
        const active = data.pending.filter(payment => !['failed','review'].includes(payment.validation_status));
        if (data.pending.length && !data.configured) {
          setValidation({ severity: 'warning', text: 'Payment validation is temporarily unavailable. Your payment reference is saved; please contact support.' });
        } else if (active.length) {
          delay = 5000;
          setValidation({ severity: 'info', text: 'Validating your payment. Your account will be credited once confirmed.' });
        } else if (data.pending.some(payment => payment.validation_status === 'review')) {
          setValidation({ severity: 'warning', text: 'Your payment needs a support review. Please provide the payment reference shown in Recharge wallet.' });
        } else if (data.pending.some(payment => payment.validation_status === 'failed')) {
          setValidation({ severity: 'warning', text: 'Paystack has not confirmed a successful payment. If you were charged, open Recharge wallet and check the payment reference or contact support.' });
        } else { setValidation(null); }
      } catch {
        if (!cancelled && pendingReferences.size) {
          delay = 5000;
          setValidation({ severity: 'warning', text: 'We could not refresh your payment status. Server validation will continue; reconnect to see your credited balance.' });
        }
      } finally {
        checking = false;
        if (!cancelled) timer = setTimeout(check, delay);
      }
    }
    function onQueued(event) {
      if (event.detail?.reference) pendingReferences.add(event.detail.reference);
      setValidation({ severity: 'info', text: 'Validating your payment. Your account will be credited once confirmed.' });
      void check();
    }
    function onVisible() { if (document.visibilityState === 'visible') void check(); }
    window.addEventListener('fin-wallet-validation', onQueued);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    void check();
    return () => { cancelled = true; clearTimeout(timer); window.removeEventListener('fin-wallet-validation', onQueued); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', onVisible); };
  }, [token, role, accesstoken, setRefresh, setRefreshAmount]);

  if (role !== '0') return null;
  return <div className="fin-wallet-payment-status">
    {validation && <Alert severity={validation.severity} role="status" icon={validation.severity === 'info' ? <CircularProgress size={18} color="inherit" /> : undefined} sx={{ marginBottom: 2 }}>{validation.text}</Alert>}
    {credited && <Alert severity="success" onClose={() => setCredited(null)} sx={{ marginBottom: 2 }}>Payment confirmed. Your account has been credited with {Number(credited.quantity).toLocaleString()} registration credits.</Alert>}
  </div>;
}
